import { Facilities, Facility, ScreenLine } from "@app/shared";
import { FC, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Pages } from "..";
import { useFetch } from "../../../hooks";
import { DashboardLayout } from "../../layouts";
import { TabbedDashLayout } from "../../layouts/TabbedDashLayout";
import { ReportsView } from "../../reports/ReportsView";
import { MissingResourceNotice, SkeletonGrid } from "../../shared";
import { RequiresGoogleMaps } from "../../shared/maps/RequiresGoogleMaps";
import { NavSelectorDropdown } from "../../shared/NavSelectorDropdown";
import { FacilityDetailsInfo } from "./FacilityDetailsInfo";
import { FacilityDevices } from "./FacilityDevices";
import { FacilityEvents } from "./FacilityEvents";
import { FacilityIntegrations } from "./FacilityIntegrations";
import { FacilityLayoutInfo } from "./FacilityLayoutInfo";
import { FacilityLiveView } from "./FacilityLiveView";
import { FacilityParkingView } from "./FacilityParkingView";

const defaultShapePoints = (facility: Facility | undefined) => {
    if (!facility) return;
    FixNanDefaultFacilityScreenLines(facility.screenLines ?? [])
}

const FixNanDefaultFacilityScreenLines = (screenLines: ScreenLine[]) => {
    screenLines.forEach(s => {
        if (s.points) {
            s.points = s.points.filter(p => typeof p.x === 'number' && !isNaN(p.x) && typeof p.y === 'number' && !isNaN(p.y));
        }
    });
};

export const FacilityDetails: FC = (props) => {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const fromAlert = searchParams.get("ref") === "alert";
    // Alert-email facility links carry the originating device id so we can offer a
    // "view the device" fallback when the location itself can't be shown (#772).
    const alertDeviceId = searchParams.get("device") ?? undefined;
    const [facility, , helpers] = useFetch(() => Facilities.getById(id), [id]);

    useEffect(() => {
        if (facility?.id) {
            defaultShapePoints(facility);
        }
    }, [facility])

    if (helpers.isLoading || helpers.isLoading === undefined) {
        return (
            <DashboardLayout centerChildren={"Loading..."}>
                <SkeletonGrid rows={3} cols={1} />
            </DashboardLayout>
        );
    }

    // The location couldn't be loaded: it was deleted, the device was moved out of it,
    // or the user doesn't have access. Explain instead of silently redirecting (#772).
    if (!facility || !facility.id) {
        return (
            <MissingResourceNotice
                title="Location not available"
                message={fromAlert
                    ? "This alert was set up for a location that's no longer available — the location may have been removed, or the device may have been moved out of it. You can still open the device that generated this alert."
                    : "This location couldn't be found. It may have been removed, or you may not have access to it."}
                actions={[
                    ...(fromAlert && alertDeviceId
                        ? [{ label: "View the device", to: Pages.DeviceDetails.getUrl({ id: alertDeviceId }) + "?ref=alert", primary: true }]
                        : []),
                    { label: "Go to my locations", to: Pages.ViewFacilityGroups.getUrl(), primary: !(fromAlert && alertDeviceId) },
                ]}
            />
        );
    }

    var key = 1;

    return (
        <TabbedDashLayout
            leftChildren={
                <NavSelectorDropdown
                    mode="groups-locations"
                    triggerLabel={facility.name ?? ""}
                    initialOrgId={facility.organizationId}
                    initialGroupId={facility.facilityGroupId}
                    initialFacilityId={id}
                />
            }
            tabs={[
                { title: "Details", child: <FacilityDetailsInfo key={key++} facility={facility} onChange={helpers.refreshData} /> },
                { title: "Recent Targets", child: <RequiresGoogleMaps><FacilityLiveView key={key++} facility={facility} /></RequiresGoogleMaps> },
                { title: "Parking View", child: <RequiresGoogleMaps><FacilityParkingView key={key++} facility={facility} /></RequiresGoogleMaps> },
                { title: "Layout", child: <FacilityLayoutInfo key={key++} facility={facility} onChange={helpers.refreshData} /> },
                { title: "Devices", child: <FacilityDevices key={key++} facility={facility} onChange={helpers.refreshData} /> },
                { title: "Reports", child: <ReportsView key={key++} facility={facility} onChange={helpers.refreshData} /> },
                { title: "Integrations", child: <FacilityIntegrations key={key++} facility={facility} onChange={helpers.refreshData} /> },
                { title: "Events", child: <RequiresGoogleMaps><FacilityEvents key={key++} facility={facility} onChange={helpers.refreshData} /></RequiresGoogleMaps> }]}
            centerChildren={facility?.name}
        />
    );
}
